import { toNumber } from '../../utils/numberUtils.js'
import { toYearMonth, monthKey } from '../../utils/dateUtils.js'
import { validarCUITModulo11 } from '../../utils/validacionCUIT.js'
import CORE_RULES_CONFIG from '../rules/config.js'

export const CUTOFF_DOCUMENTAL = '2025-03'

function cleanNoise(input) {
  const txt = String(input || '')
  return txt
    .replace(/Automa|matizado|Gal|Gaal/gi, '')
    .replace(/[^\x00-\x7F]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function normalizeEmployerToken(value) {
  return cleanNoise(value)
    .toUpperCase()
    .replace(/4/g, 'A')
    .replace(/0/g, 'O')
    .replace(/1/g, 'I')
    .replace(/[^A-Z0-9 ]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

function levenshtein(a, b) {
  const s = String(a || '')
  const t = String(b || '')
  const dp = Array.from({ length: s.length + 1 }, (_, i) => [i])
  for (let j = 1; j <= t.length; j += 1) dp[0][j] = j
  for (let i = 1; i <= s.length; i += 1) {
    for (let j = 1; j <= t.length; j += 1) {
      const cost = s[i - 1] === t[j - 1] ? 0 : 1
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + cost)
    }
  }
  return dp[s.length][t.length]
}

function unifyEmployerName(rawName, canonicalMap) {
  const cleaned = cleanNoise(rawName || '')
  const token = normalizeEmployerToken(cleaned)
  if (!token) return cleaned
  const known = Array.from(canonicalMap.keys())
  const match = known.find((k) => levenshtein(k, token) <= 1)
  if (match) return canonicalMap.get(match)
  canonicalMap.set(token, cleaned)
  return cleaned
}

function nextMonthKey(prevKey) {
  const ym = toYearMonth(prevKey)
  if (!ym) return ''
  let y = Number(String(ym).slice(0, 4))
  let m = Number(String(ym).slice(4, 6))
  m += 1
  if (m > 12) {
    m = 1
    y += 1
  }
  return `${y}-${String(m).padStart(2, '0')}`
}

function buildDetalleSimultaneidad(row) {
  return {
    cuit: String(row.cuit || '').trim() || 'S/D',
    empresa: String(row.empresa || row.razonSocial || '').trim() || 'S/D',
    monto: toNumber(row.monto ?? row.remImpSS ?? row.montoOriginal),
  }
}

function isMissingCuit(value) {
  const cuit = String(value || '').trim().toUpperCase()
  return !cuit || cuit === 'SIN_DATO' || cuit === 'NULL' || cuit === 'N/A'
}

function validarCuitOCR(value) {
  const raw = String(value || '').trim()
  if (isMissingCuit(raw)) {
    return {
      cuitSanitizado: 'SIN_DATO',
      cuitValidoModulo11: false,
      cuitCorrupto: false,
      cuitOriginalFallido: '',
      cuitMotivoError: '',
    }
  }

  const resultado = validarCUITModulo11(raw, {
    origen: 'sanitizer',
    datosCompletos: false,
  })

  if (resultado?.valido && resultado?.cuitNormalizado) {
    const digits = String(resultado.cuitNormalizado).replace(/\D/g, '')
    return {
      cuitSanitizado: digits || 'SIN_DATO',
      cuitValidoModulo11: true,
      cuitCorrupto: false,
      cuitOriginalFallido: '',
      cuitMotivoError: '',
    }
  }

  const errorMessage = typeof resultado?.error === 'string'
    ? resultado.error
    : String(resultado?.error?.mensaje || resultado?.error?.message || 'fallo_validacion_modulo11')

  return {
    cuitSanitizado: 'SIN_DATO',
    cuitValidoModulo11: false,
    cuitCorrupto: true,
    cuitOriginalFallido: raw,
    cuitMotivoError: errorMessage,
  }
}

function aplicarHeuristicaIdentidadEmpleador(rows = []) {
  const canonicalByEmployer = new Map()
  rows.forEach((row) => {
    const employerToken = normalizeEmployerToken(row.razonSocial || row.empresa || '')
    const cuit = String(row.cuit || '').trim()
    if (!employerToken || isMissingCuit(cuit) || !row.cuitValidoModulo11) return
    if (!canonicalByEmployer.has(employerToken)) canonicalByEmployer.set(employerToken, cuit)
  })

  return rows.map((row) => {
    const employerToken = normalizeEmployerToken(row.razonSocial || row.empresa || '')
    const cuit = String(row.cuit || '').trim()
    if (!employerToken || !isMissingCuit(cuit)) return row
    const matchedCuit = canonicalByEmployer.get(employerToken)
    if (!matchedCuit) return row
    return {
      ...row,
      cuit: matchedCuit,
      cuitRecuperado: matchedCuit,
      requiereRevision: true,
      requiresValidation: true,
      observacionSanitizador: row.observacionSanitizador
        ? `${row.observacionSanitizador}; CUIT inferido por heuristica de identidad de empleador`
        : 'CUIT inferido por heuristica de identidad de empleador',
    }
  })
}

function resolveIdentityScore(row) {
  const weights = CORE_RULES_CONFIG.certaintyWeights || { FACTUAL: 100, INFERRED: 70, CORRUPT: 30 }
  const factual = Boolean(row?.cuitValidoModulo11)
  const inferred = Boolean(row?.cuitRecuperado)
  const corrupt = Boolean(row?.cuitCorrupto && !row?.cuitRecuperado)
  if (factual) return { estadoIdentidad: 'FACTUAL', confianzaIdentidad: Number(weights.FACTUAL || 100) }
  if (inferred) return { estadoIdentidad: 'INFERRED', confianzaIdentidad: Number(weights.INFERRED || 70) }
  if (corrupt) return { estadoIdentidad: 'CORRUPT', confianzaIdentidad: Number(weights.CORRUPT || 30) }
  return { estadoIdentidad: 'CORRUPT', confianzaIdentidad: Number(weights.CORRUPT || 30) }
}

export function consolidarSimultaneidad(remuneraciones = [], activeRules = null) {
  const rows = Array.isArray(remuneraciones) ? remuneraciones : []
  const sumar = activeRules?.protocol?.sumarSimultaneidad !== false
  if (!sumar) return rows

  const byPeriodo = new Map()
  rows.forEach((row) => {
    const key = String(row.periodoKey || '')
    if (!key) return
    const bucket = byPeriodo.get(key) || []
    bucket.push(row)
    byPeriodo.set(key, bucket)
  })

  const out = []
  byPeriodo.forEach((bucket) => {
    if (!bucket.length) return

    const byCuit = new Map()
    bucket.forEach((row) => {
      const cuitBase = String(row.cuit || '').trim()
      const employerFallback = normalizeEmployerToken(row.empresa || row.razonSocial || '') || 'SIN_DATO'
      const cuitKey = !isMissingCuit(cuitBase) ? cuitBase : `SIN_CUIT::${employerFallback}`
      const cuitBucket = byCuit.get(cuitKey) || []
      cuitBucket.push(row)
      byCuit.set(cuitKey, cuitBucket)
    })

    const cuitGroups = Array.from(byCuit.values())
    const haySimultaneidadReal = cuitGroups.length > 1

    cuitGroups.forEach((cuitBucket) => {
      const base = cuitBucket[0]
      const detalle = cuitBucket.map(buildDetalleSimultaneidad)
      const empresas = Array.from(new Set(detalle.map((d) => d.empresa).filter(Boolean)))
      const montoSumado = detalle.reduce((acc, d) => acc + toNumber(d.monto), 0)
      const huboFusionMismoCuit = cuitBucket.length > 1
      // Gap #3: preservar el máximo de días computables entre filas fusionadas
      const maxDiasCuitBucket = cuitBucket.reduce((best, r) => {
        const d = Number.isInteger(r.diasComputables) ? r.diasComputables : -1
        return d > best ? d : best
      }, -1)

      out.push({
        ...base,
        cuit: String(base.cuit || '').trim() || 'SIN_DATO',
        monto: montoSumado,
        remImpSS: montoSumado,
        montoOriginal: montoSumado,
        empresa: empresas.join(' + ') || String(base.empresa || base.razonSocial || ''),
        razonSocial: empresas.join(' + ') || String(base.razonSocial || base.empresa || ''),
        diasComputables: maxDiasCuitBucket >= 0 ? maxDiasCuitBucket : (base.diasComputables ?? null),
        reglaAplicadaDias: huboFusionMismoCuit ? 'fusion_multi_cuit_max_dias' : base.reglaAplicadaDias,
        dobleEmpleo: haySimultaneidadReal,
        detalleSimultaneidad: detalle,
        observacionSanitizador: huboFusionMismoCuit
          ? 'Ajustes/retroactivos fusionados por clave unica (periodo + CUIT)'
          : haySimultaneidadReal
            ? 'Simultaneidad real: CUIT distinto en el mismo periodo'
            : base.observacionSanitizador,
        requiereRevision: Boolean(base.requiereRevision || huboFusionMismoCuit),
        requiresValidation: Boolean(base.requiresValidation || huboFusionMismoCuit),
      })
    })
  })
  return out
}

export function sanitizarRemuneraciones(remuneraciones = [], fechaCese = '', ultimoPeriodoDetectado = '', activeRules = null) {
  const rows = Array.isArray(remuneraciones) ? remuneraciones : []
  const now = new Date()
  const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  const currentYm = toYearMonth(currentKey)
  const ceseKey = monthKey(fechaCese)
  const ceseYm = toYearMonth(ceseKey)

  const maxAporteRealYm = rows.reduce((acc, row) => {
    const monto = toNumber(row.monto ?? row.remImpSS ?? row.montoOriginal)
    if (!(monto > 0)) return acc
    const key = monthKey(row.periodoKey || row.periodo || row.periodoISO)
    const ym = toYearMonth(key)
    if (!ym) return acc
    return ym > acc ? ym : acc
  }, 0)

  const ultimoDetectadoYm = ultimoPeriodoDetectado ? toYearMonth(monthKey(ultimoPeriodoDetectado)) : 0
  const techoTemporalYm = Math.max(ceseYm || 0, maxAporteRealYm || 0, ultimoDetectadoYm || 0)

  const validRows = []
  const suspiciousRows = []
  const employerCanonicalMap = new Map()
  let lastValidKey = ''

  rows.forEach((row) => {
    const rawPeriodo = String(row.periodo || '')
    const key = row.periodoKey || monthKey(row.periodo || row.periodoISO)
    let fixedKey = key
    let fechaReconstruida = false
    if (!fixedKey && /^\d{2}\/\d{3}$/.test(rawPeriodo) && lastValidKey) {
      fixedKey = nextMonthKey(lastValidKey)
      fechaReconstruida = true
    }

    const ym = toYearMonth(fixedKey)
    const monto = toNumber(row.monto ?? row.remImpSS ?? row.montoOriginal)
    const hasNumericMonto = Number.isFinite(Number(row.monto ?? row.remImpSS ?? row.montoOriginal))
    const isMontoSospechoso = Boolean(ym && ym >= 201001 && monto > 0 && monto < 100)
    const isFechaInvalida = !fixedKey || !ym
    const isFuturo = Boolean(currentYm && ym && ym > currentYm)
    const isPosteriorCese = Boolean(techoTemporalYm && ym && ym > techoTemporalYm)

    const empresaUnificada = unifyEmployerName(row.empresa || row.razonSocial || '', employerCanonicalMap)
    const cuit = String(row.cuit || '').trim()
    const validacionCUIT = validarCuitOCR(cuit)
    const baseRow = {
      ...row,
      periodoKey: fixedKey || String(row.periodoKey || ''),
      periodoISO: fixedKey || String(row.periodoISO || ''),
      periodo: fixedKey ? `${fixedKey.slice(5, 7)}/${fixedKey.slice(0, 4)}` : String(row.periodo || ''),
      empresa: cleanNoise(empresaUnificada),
      razonSocial: cleanNoise(empresaUnificada),
      cuit: validacionCUIT.cuitSanitizado,
      cuitValidoModulo11: validacionCUIT.cuitValidoModulo11,
      cuitCorrupto: validacionCUIT.cuitCorrupto,
      cuitOriginalFallido: validacionCUIT.cuitOriginalFallido,
      cuitMotivoError: validacionCUIT.cuitMotivoError,
      cuitRecuperado: '',
      monto,
      remImpSS: toNumber(row.remImpSS || monto),
      montoOriginal: toNumber(row.montoOriginal || monto),
      regimenContributivo: String(row.regimenContributivo || ''),
      requiresValidation: Boolean(row.requiresValidation || fechaReconstruida || isMontoSospechoso),
      requiereRevision: Boolean(row.requiereRevision || fechaReconstruida || isMontoSospechoso),
      observacionMonto: isMontoSospechoso ? 'Monto bajo detectado, requiere validacion manual' : undefined,
      observacionSanitizador: validacionCUIT.cuitCorrupto
        ? (row.observacionSanitizador
          ? `${row.observacionSanitizador}; CUIT invalido por modulo 11`
          : 'CUIT invalido por modulo 11')
        : row.observacionSanitizador,
    }

    if (isFechaInvalida || isFuturo || isPosteriorCese) {
      if (hasNumericMonto) {
        suspiciousRows.push({
          ...baseRow,
          isSuspicious: true,
          techoTemporalYm,
          suspiciousReason: [
            isFechaInvalida ? 'fecha_invalida' : '',
            isFuturo ? 'periodo_futuro' : '',
            isPosteriorCese ? 'posterior_a_cese' : '',
          ].filter(Boolean).join(','),
        })
      }
      return
    }

    lastValidKey = fixedKey
    validRows.push(baseRow)
  })

  const withIdentityHeuristic = aplicarHeuristicaIdentidadEmpleador(validRows)
  const consolidado = consolidarSimultaneidad(withIdentityHeuristic, activeRules)

  const mergedAll = [...consolidado, ...suspiciousRows].map((row) => {
    const score = resolveIdentityScore(row)
    return {
      ...row,
      estadoIdentidad: score.estadoIdentidad,
      confianzaIdentidad: score.confianzaIdentidad,
    }
  })

  // ═══════════════════════════════════════════════════════════
  //  Fusión de CUITs simultáneos (multi-empleo)
  //  Cuando un período tiene 2+ CUITs con dobleEmpleo: true,
  //  los sumamos en UNA sola fila consolidada en vez de
  //  que bestByPeriod descarte las de menor monto.
  //  Fix #1 + #5 combinado.
  // ═══════════════════════════════════════════════════════════
  const rowsAfterFusion = []
  const fusionBuckets = new Map()
  for (let i = 0; i < mergedAll.length; i++) {
    const row = mergedAll[i]
    if (row.dobleEmpleo && row.periodoKey) {
      const bucket = fusionBuckets.get(row.periodoKey)
      if (bucket) {
        bucket.push(row)
      } else {
        fusionBuckets.set(row.periodoKey, [row])
      }
    } else {
      rowsAfterFusion.push(row)
    }
  }
  for (const [, bucket] of fusionBuckets) {
    if (bucket.length <= 1) {
      rowsAfterFusion.push(bucket[0])
    } else {
      // Gap #4 fix: agrupar por tipoActividad en vez de colapsar en bucket[0]
      // Cada tipo produce su propia fila preservando tipoActividad y regimenContributivo
      const byTipo = new Map()
      bucket.forEach((r) => {
        const tipo = String(r.tipoActividad || r.regimenContributivo || '').toLowerCase() || '__default__'
        const group = byTipo.get(tipo) || []
        group.push(r)
        byTipo.set(tipo, group)
      })
      byTipo.forEach((typeBucket, tipo) => {
        const base = { ...typeBucket[0] }
        const fusionMismoTipo = typeBucket.length > 1
        base.monto = typeBucket.reduce((s, r) => s + (Number(r.monto) || 0), 0)
        base.remImpSS = typeBucket.reduce((s, r) => s + (Number(r.remImpSS || r.monto) || 0), 0)
        base.montoOriginal = typeBucket.reduce((s, r) => s + (Number(r.montoOriginal || r.monto) || 0), 0)
        base.empresa = typeBucket.map(r => r.empresa || r.razonSocial || '').filter(Boolean).join(' + ')
        base.razonSocial = base.empresa
        base.detalleSimultaneidad = typeBucket.flatMap(r => r.detalleSimultaneidad || [])
        // Gap #3: preservar el máximo de días computables entre CUITs simultáneos
        const maxDiasMultiCuit = typeBucket.reduce((best, r) => {
          const d = Number.isInteger(r.diasComputables) ? r.diasComputables : -1
          return d > best ? d : best
        }, -1)
        base.diasComputables = maxDiasMultiCuit >= 0 ? maxDiasMultiCuit : (base.diasComputables ?? null)
        base.reglaAplicadaDias = fusionMismoTipo ? 'fusion_multi_cuit_max_dias' : (typeBucket[0].reglaAplicadaDias || '')
        base.observacionSanitizador = fusionMismoTipo
          ? `Multi-CUIT sumado: ${typeBucket.length} CUITs simultáneos fusionados en un solo registro`
          : `Simultaneidad real: CUIT distinto en el mismo periodo (${tipo})`
        rowsAfterFusion.push(base)
      })
    }
  }
  // ═══════════════════════════════════════════════════════════

  // Gap #4 fix: clave compuesta periodo+tipoActividad para permitir
  // una fila por tipo en el mismo período
  const bestByPeriod = new Map()
  rowsAfterFusion.forEach((row) => {
    const key = row.periodoKey
    if (!key) return
    const tipo = String(row.tipoActividad || '').toLowerCase() || '__default__'
    const compoundKey = `${key}::${tipo}`
    const existing = bestByPeriod.get(compoundKey)
    if (!existing || (Number(row.monto) || 0) > (Number(existing.monto) || 0)) {
      bestByPeriod.set(compoundKey, row)
    }
  })

  const sinColision = []
  const colisiones = []
  rowsAfterFusion.forEach((row) => {
    const key = row.periodoKey
    if (!key) { sinColision.push(row); return }
    const tipo = String(row.tipoActividad || '').toLowerCase() || '__default__'
    const compoundKey = `${key}::${tipo}`
    if (row === bestByPeriod.get(compoundKey)) {
      sinColision.push(row)
    } else {
      colisiones.push({
        ...row,
        isSuspicious: true,
        suspiciousReason: 'colision_periodo',
        observacionSanitizador: 'Colision de periodo: descartado por tener menor monto que otra fila del mismo periodo',
      })
    }
  })

  return [...sinColision, ...colisiones].sort((a, b) => {
    const ay = toYearMonth(a.periodoKey || '') || 0
    const by = toYearMonth(b.periodoKey || '') || 0
    if (ay !== by) return by - ay
    return (Number(b.monto) || 0) - (Number(a.monto) || 0)
  })
}
